/**
 * CampsBrowsePage — /camps
 *
 * Implements WI-49: cross-school upcoming ID camps browse page.
 * WI-52: "My Schools" watchlist filter chip.
 *
 * Layout:
 *   - AppHeader (shared)
 *   - Page heading
 *   - Filter rail: sport chips · division chips · region chip · state chips · My Schools chip
 *   - aria-live result count
 *   - Loading skeleton / empty states / camp cards grouped by month
 *   - "Load more" button (appends next 25 results)
 *
 * URL round-trip: ?sport=...&division=...&region=...&state=...&watchlist=true
 *
 * Accessibility:
 *   - aria-live="polite" result count
 *   - aria-pressed on sport + division + My Schools chips
 *   - Semantic <ul><li> camp list
 *   - <h3> month headers (h1=page, h2=filter+results area)
 *   - Register links have aria-label="Register for <camp.name>"
 *   - WCAG 2.1 AA
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { AppHeader } from './AppHeader';
import { useAuth } from '../hooks/useAuth';
import { useWatchlist } from '../hooks/useWatchlist';
import { fetchCampsBrowse, BrowseCampItem, CampsUnauthenticatedError } from '../api/camps';
import { BrowseCampCard } from './camps/BrowseCampCard';
import { AuthPromptSheet } from './AuthPromptSheet';

// ── Constants ─────────────────────────────────────────────────────────────────

const PAGE_SIZE = 25;

const SPORT_OPTIONS = [
  { slug: 'soccer_mens', label: "Men's Soccer" },
  { slug: 'soccer_womens', label: "Women's Soccer" },
  { slug: 'football_mens', label: "Men's Football" },
  { slug: 'basketball_mens', label: "Men's Basketball" },
  { slug: 'basketball_womens', label: "Women's Basketball" },
] as const;

const DIVISION_OPTIONS = [
  { slug: 'ncaa_d1', label: 'D1' },
  { slug: 'ncaa_d2', label: 'D2' },
  { slug: 'ncaa_d3', label: 'D3' },
  { slug: 'naia', label: 'NAIA' },
  { slug: 'njcaa', label: 'NJCAA' },
] as const;

const REGION_OPTIONS = [
  { slug: 'Northeast', label: 'Northeast' },
  { slug: 'Midwest', label: 'Midwest' },
  { slug: 'South', label: 'South' },
  { slug: 'West', label: 'West' },
] as const;

/** Today as YYYY-MM-DD in local time. */
function todayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Format a YYYY-MM key as "Month YYYY". */
function formatMonthHeader(yearMonth: string): string {
  // Append day + time to avoid UTC offset shifting the month
  return new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(
    new Date(`${yearMonth}-01T00:00:00`),
  );
}

/** Sentinel key for the "Date to be announced" group. Always sorts last. */
const DATE_TBD_KEY = 'date-tbd';

/**
 * Group an already-sorted array of camps by their YYYY-MM month key.
 * Hub-link rows (date_unknown = true) are bucketed under DATE_TBD_KEY and
 * always placed at the end, regardless of their sentinel start_date. WI-51b.
 */
function groupByMonth(camps: BrowseCampItem[]): Array<{ monthKey: string; camps: BrowseCampItem[] }> {
  const groups: Array<{ monthKey: string; camps: BrowseCampItem[] }> = [];
  let tbdGroup: { monthKey: string; camps: BrowseCampItem[] } | null = null;

  for (const camp of camps) {
    if (camp.date_unknown) {
      // Bucket hub-link rows into the TBD group (always at the end).
      if (!tbdGroup) {
        tbdGroup = { monthKey: DATE_TBD_KEY, camps: [] };
      }
      tbdGroup.camps.push(camp);
    } else {
      const key = camp.start_date.slice(0, 7);
      const last = groups[groups.length - 1];
      if (last && last.monthKey === key) {
        last.camps.push(camp);
      } else {
        groups.push({ monthKey: key, camps: [camp] });
      }
    }
  }

  // Append the TBD group at the end if it has any items.
  if (tbdGroup) {
    groups.push(tbdGroup);
  }

  return groups;
}

// ── CampsBrowsePage ───────────────────────────────────────────────────────────

export function CampsBrowsePage() {
  const { user, loading: authLoading } = useAuth();
  const isAuthenticated = !!user;
  const resolvedAuthenticated = authLoading ? false : isAuthenticated;

  // useWatchlist eagerly for authenticated users — needed for watchlist empty-state logic.
  const { savedIds } = useWatchlist(resolvedAuthenticated);

  const [searchParams, setSearchParams] = useSearchParams();

  // ── AuthPromptSheet state ─────────────────────────────────────────────────

  const [authSheetOpen, setAuthSheetOpen] = useState(false);

  // ── Derive filter state from URL ──────────────────────────────────────────

  const sport = searchParams.get('sport') ?? 'soccer_mens';
  const divisionParam = searchParams.get('division');
  const regionParam = searchParams.get('region') ?? '';
  const stateParam = searchParams.get('state');
  const watchlistParam = searchParams.get('watchlist') === 'true';

  const selectedDivisions: string[] = divisionParam
    ? divisionParam.split(',').filter(Boolean)
    : [];
  const selectedRegion = regionParam;
  const selectedStates: string[] = stateParam
    ? stateParam.split(',').filter(Boolean)
    : [];

  // watchlist filter is only active when the user is authenticated
  const watchlistActive = watchlistParam && resolvedAuthenticated;

  const hasActiveFilters =
    selectedDivisions.length > 0 ||
    selectedRegion !== '' ||
    selectedStates.length > 0 ||
    watchlistParam;

  // ── Result state ──────────────────────────────────────────────────────────

  const [camps, setCamps] = useState<BrowseCampItem[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasMore = currentPage * PAGE_SIZE < total;

  // ── Fetch on filter change ────────────────────────────────────────────────

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setCurrentPage(1);

    fetchCampsBrowse({
      sport,
      from: todayIso(),
      division: selectedDivisions.length > 0 ? selectedDivisions : undefined,
      region: selectedRegion || undefined,
      state: selectedStates.length > 0 ? selectedStates : undefined,
      page: 1,
      page_size: PAGE_SIZE,
      watchlist: watchlistActive || undefined,
    })
      .then((data) => {
        if (cancelled) return;
        setCamps(data.camps);
        setTotal(data.total);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof CampsUnauthenticatedError) {
          // Session expired mid-browse — prompt the user to re-authenticate.
          setAuthSheetOpen(true);
          setCamps([]);
          setTotal(0);
        } else {
          setError(err instanceof Error ? err.message : 'Failed to load camps');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
    // Intentional: only re-run when URL-derived filter values change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sport, divisionParam, regionParam, stateParam, watchlistParam, resolvedAuthenticated]);

  // ── Load more ─────────────────────────────────────────────────────────────

  function handleLoadMore() {
    const nextPage = currentPage + 1;
    setLoadingMore(true);
    fetchCampsBrowse({
      sport,
      from: todayIso(),
      division: selectedDivisions.length > 0 ? selectedDivisions : undefined,
      region: selectedRegion || undefined,
      state: selectedStates.length > 0 ? selectedStates : undefined,
      page: nextPage,
      page_size: PAGE_SIZE,
      watchlist: watchlistActive || undefined,
    })
      .then((data) => {
        setCamps((prev) => [...prev, ...data.camps]);
        setTotal(data.total);
        setCurrentPage(nextPage);
      })
      .catch((err: unknown) => {
        if (err instanceof CampsUnauthenticatedError) {
          setAuthSheetOpen(true);
        }
      })
      .finally(() => setLoadingMore(false));
  }

  // ── Filter update helpers ─────────────────────────────────────────────────

  const updateParam = useCallback(
    (key: string, value: string | null) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value === null || value === '') {
          next.delete(key);
        } else {
          next.set(key, value);
        }
        return next;
      });
    },
    [setSearchParams],
  );

  function setSport(slug: string) {
    updateParam('sport', slug);
  }

  function toggleDivision(slug: string) {
    const next = selectedDivisions.includes(slug)
      ? selectedDivisions.filter((d) => d !== slug)
      : [...selectedDivisions, slug];
    updateParam('division', next.length > 0 ? next.join(',') : null);
  }

  function setRegion(slug: string) {
    updateParam('region', selectedRegion === slug ? null : slug);
    // Clear state when region changes
    updateParam('state', null);
  }

  function toggleWatchlist() {
    if (!resolvedAuthenticated) {
      // Open the auth prompt — do NOT modify the URL.
      setAuthSheetOpen(true);
      return;
    }
    updateParam('watchlist', watchlistParam ? null : 'true');
  }

  function clearFilters() {
    setSearchParams((prev) => {
      const next = new URLSearchParams();
      if (prev.get('sport')) next.set('sport', prev.get('sport')!);
      return next;
    });
  }

  // ── Grouped camps ─────────────────────────────────────────────────────────

  const groupedCamps = groupByMonth(camps);

  // ── Live count announcement ───────────────────────────────────────────────

  const countAnnouncement = loading
    ? 'Loading camps…'
    : `Showing ${camps.length} of ${total} upcoming camp${total !== 1 ? 's' : ''}`;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-surface-base">
      <AppHeader isAuthenticated={resolvedAuthenticated} />

      {/* AuthPromptSheet — shown when unauthenticated user clicks My Schools chip,
          or when the server returns 401 (session expired). The return URL includes
          ?watchlist=true so the chip activates automatically after login. */}
      <AuthPromptSheet
        isOpen={authSheetOpen}
        onClose={() => setAuthSheetOpen(false)}
      />

      <div className="max-w-3xl mx-auto px-4 py-6">

        {/* Page heading */}
        <div className="mb-5">
          <h1 className="text-2xl font-bold tracking-tight text-gray-900">
            ID Camps
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Upcoming identification camps — browse by sport, division, and location.
          </p>
        </div>

        {/* ── Filter rail ──────────────────────────────────────────────── */}
        <div className="flex flex-col gap-3 mb-5">

          {/* Sport chips */}
          <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filter by sport">
            {SPORT_OPTIONS.map((opt) => {
              const isSelected = sport === opt.slug;
              return (
                <button
                  key={opt.slug}
                  type="button"
                  aria-pressed={isSelected}
                  onClick={() => setSport(opt.slug)}
                  className={`px-3 py-1.5 text-xs rounded-full border font-medium
                              min-h-[44px] md:min-h-0
                              motion-safe:transition-colors
                              focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-1
                              ${isSelected
                                ? 'bg-brand-primary border-brand-primary text-white'
                                : 'bg-white border-gray-300 text-gray-700 hover:border-brand-primary'
                              }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>

          {/* Division chips */}
          <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filter by division">
            {DIVISION_OPTIONS.map((opt) => {
              const isSelected = selectedDivisions.includes(opt.slug);
              return (
                <button
                  key={opt.slug}
                  type="button"
                  aria-pressed={isSelected}
                  onClick={() => toggleDivision(opt.slug)}
                  className={`px-2.5 py-1 text-xs rounded-full border font-medium
                              min-h-[44px] md:min-h-0
                              motion-safe:transition-colors
                              focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-1
                              ${isSelected
                                ? 'bg-brand-primary border-brand-primary text-white'
                                : 'bg-white border-gray-300 text-gray-700 hover:border-brand-primary'
                              }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>

          {/* Region chips */}
          <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filter by region">
            {REGION_OPTIONS.map((opt) => {
              const isSelected = selectedRegion === opt.slug;
              return (
                <button
                  key={opt.slug}
                  type="button"
                  aria-pressed={isSelected}
                  onClick={() => setRegion(opt.slug)}
                  className={`px-2.5 py-1 text-xs rounded-full border font-medium
                              min-h-[44px] md:min-h-0
                              motion-safe:transition-colors
                              focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-1
                              ${isSelected
                                ? 'bg-brand-primary border-brand-primary text-white'
                                : 'bg-white border-gray-300 text-gray-700 hover:border-brand-primary'
                              }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>

          {/* My Schools chip (WI-52) */}
          <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filter by watchlist">
            <button
              type="button"
              aria-pressed={watchlistActive}
              aria-label={
                resolvedAuthenticated
                  ? 'My Schools'
                  : 'My Schools — sign in required'
              }
              title={resolvedAuthenticated ? undefined : 'Sign in to filter by your saved schools'}
              onClick={toggleWatchlist}
              className={`px-2.5 py-1 text-xs rounded-full border font-medium
                          min-h-[44px] md:min-h-0
                          motion-safe:transition-colors
                          focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-1
                          ${watchlistActive
                            ? 'bg-brand-primary border-brand-primary text-white'
                            : 'bg-white border-gray-300 text-gray-700 hover:border-brand-primary'
                          }`}
            >
              My Schools
              {!resolvedAuthenticated && (
                <span aria-hidden="true" className="ml-1">🔒</span>
              )}
            </button>
          </div>
        </div>

        {/* ── Results area ─────────────────────────────────────────────── */}
        <div>
          <h2 className="sr-only">Camps results</h2>

          {/* aria-live result count */}
          <p
            aria-live="polite"
            aria-atomic="true"
            className="text-sm text-gray-500 tabular-nums mb-3"
          >
            {countAnnouncement}
          </p>

          {/* Error state */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Loading skeleton */}
          {loading && (
            <div className="flex flex-col gap-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-[88px] rounded-lg bg-white border border-gray-200 animate-pulse" />
              ))}
            </div>
          )}

          {/* Watchlist-empty empty state (AC-8): chip on + user has zero watchlist entries */}
          {!loading && !error && total === 0 && watchlistActive && savedIds.size === 0 && (
            <div className="text-center py-12 text-sm text-gray-600">
              <p className="font-semibold text-gray-700 mb-3">
                Save schools to filter their camps here.
              </p>
              <Link
                to="/browse"
                className="text-brand-primary hover:underline
                           focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary rounded"
              >
                Browse schools →
              </Link>
            </div>
          )}

          {/* Watchlist-no-camps empty state (AC-9): chip on + user has schools + no camps */}
          {!loading && !error && total === 0 && watchlistActive && savedIds.size > 0 && (
            <div className="text-center py-12 text-sm text-gray-600">
              <p className="font-semibold text-gray-700 mb-1">
                No upcoming camps on record for your saved schools.
              </p>
              <p>
                We add camps weekly. Check back soon.
              </p>
            </div>
          )}

          {/* No-coverage empty state: zero results, no active user filters */}
          {!loading && !error && total === 0 && !hasActiveFilters && (
            <div className="text-center py-12 text-sm text-gray-600">
              <p className="font-semibold text-gray-700 mb-1">
                No upcoming camps on record for this sport.
              </p>
              <p className="mb-4">
                Coverage grows as we add more schools.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/browse"
                  className="text-brand-primary hover:underline
                             focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary rounded"
                >
                  Browse all schools →
                </Link>
                <span className="text-gray-500">
                  or check individual school pages for camp information
                </span>
              </div>
            </div>
          )}

          {/* Filter-empty state: zero results with active non-watchlist filters,
              or watchlist is active but the empty state is not watchlist-specific */}
          {!loading && !error && total === 0 && hasActiveFilters && !watchlistActive && (
            <div className="text-center py-12 text-sm text-gray-600">
              <p className="font-semibold text-gray-700 mb-3">
                No upcoming camps match your filters.
              </p>
              <button
                type="button"
                onClick={clearFilters}
                className="text-brand-primary hover:underline
                           focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary rounded"
              >
                Clear filters
              </button>
              {' '}
              <span>to see all upcoming camps.</span>
            </div>
          )}

          {/* Camp cards grouped by month */}
          {!loading && !error && camps.length > 0 && (
            <>
              {groupedCamps.map(({ monthKey, camps: monthCamps }) => {
                const isTbd = monthKey === DATE_TBD_KEY;
                const headingText = isTbd ? 'Date to be announced' : formatMonthHeader(monthKey);
                const headingId = `month-${monthKey}`;
                return (
                  <section
                    key={monthKey}
                    aria-labelledby={headingId}
                    className="mb-6"
                  >
                    <h3
                      id={headingId}
                      className="text-sm font-semibold text-gray-700 mb-2"
                    >
                      {headingText}
                    </h3>
                    <ul className="flex flex-col gap-3" aria-label={`${headingText} camps`}>
                      {monthCamps.map((camp) => (
                        <li key={camp.camp_id}>
                          <BrowseCampCard camp={camp} />
                        </li>
                      ))}
                    </ul>
                  </section>
                );
              })}

              {/* Load more */}
              {hasMore && (
                <div className="mt-2 flex justify-center">
                  <button
                    type="button"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="px-5 py-2.5 min-h-[44px] bg-white border border-gray-300 rounded-lg
                               text-sm font-medium text-gray-700
                               hover:border-brand-primary hover:text-brand-primary
                               motion-safe:transition-colors
                               focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary
                               disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {loadingMore ? 'Loading…' : 'Load more'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
